import type { MigrationModule } from './module';
import type { MigrationProperty } from './admin';

export const MigrationStatus = {
  InProgress: 'InProgress',
  Succeeded: 'Succeeded',
  Partial: 'Partial',
  Failed: 'Failed',
} as const;

export type MigrationStatus = (typeof MigrationStatus)[keyof typeof MigrationStatus];

export const OrganizationType = {
  New: 'New',
  Existing: 'Existing',
} as const;

export type OrganizationType = (typeof OrganizationType)[keyof typeof OrganizationType];

export const MigrationLogLevel = {
  Info: 'Info',
  Warning: 'Warning',
  Error: 'Error',
} as const;

export type MigrationLogLevel = (typeof MigrationLogLevel)[keyof typeof MigrationLogLevel];

export const UserMigrationPhaseStatus = {
  NotApplicable: 'NotApplicable',
  Pending: 'Pending',
  InProgress: 'InProgress',
  Succeeded: 'Succeeded',
  Partial: 'Partial',
  Failed: 'Failed',
} as const;

export type UserMigrationPhaseStatus = (typeof UserMigrationPhaseStatus)[keyof typeof UserMigrationPhaseStatus];

export interface Migration {
  id: string;
  organizationType: OrganizationType;
  bridgeEntityUuid: string;
  bridgeEntityName: string | null;
  amplifyOrganizationUuid: string | null;
  organizationName: string;
  streetAddress: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  ownerFirstName: string | null;
  ownerLastName: string | null;
  ownerEmail: string | null;
  ownerPhone: string | null;
  ownerUuid: string | null;
  billing: string | null;
  sendInvite: boolean | null;
  euid: string | null;
  status: MigrationStatus;
  startedAt: string;
  completedAt: string | null;
  startedByUserId: string;
  startedByUserName: string;
  modules: MigrationModule[];
  userMigrationPhaseStatus: UserMigrationPhaseStatus;
  totalUniqueUsers: number;
  totalOnboardedSuccessfully: number;
  totalOnboardFailed: number;
  totalInvitesSent: number;
  totalInviteFailed: number;
  /** Onboarded users whose welcome invite is queued for the paced batch drain. */
  totalInvitesPending: number;
  totalRawUsersCollected: number;
  totalOrgOwnerExcluded: number;
  inviteFailureCategories: Record<string, number> | null;
}

export interface MigrationSummary {
  id: string;
  organizationType: OrganizationType;
  bridgeEntityUuid: string;
  bridgeEntityName: string | null;
  organizationName: string;
  status: MigrationStatus;
  startedAt: string;
  completedAt: string | null;
  startedByUserName: string;
  totalModules: number;
  completedModules: number;
  failedModules: number;
}

export interface MigrationLog {
  id: string;
  moduleKey: string | null;
  level: MigrationLogLevel;
  message: string;
  timestamp: string;
  /**
   * W3C TraceId captured when the log row was created. Matches App Insights
   * `operation_Id`, so operators can paste this into a KQL query
   * (`traces | where operation_Id == "..."`) to find the full backend trace.
   * Null on rows persisted before this field existed.
   */
  correlationId: string | null;
  /**
   * Orchestrator/Amplify invocation id when one was on the ambient Activity at
   * row-creation time. Useful for narrowing within a single trace. Null when
   * no such tag was present (e.g. most Phase 2 user-migration logs).
   */
  invocationId: string | null;
}

export interface StartMigrationRequest {
  organizationType: OrganizationType;
  bridgeEntityUuid: string;
  bridgeEntityName?: string | undefined;
  amplifyOrganizationUuid?: string | undefined;
  organizationName: string;
  streetAddress?: string | undefined;
  addressLine2?: string | undefined;
  city?: string | undefined;
  state?: string | undefined;
  zipCode?: string | undefined;
  ownerFirstName?: string | undefined;
  ownerLastName?: string | undefined;
  ownerEmail?: string | undefined;
  ownerPhone?: string | undefined;
  billing?: string | undefined;
  sendInvite?: boolean | undefined;
  euid?: string | undefined;
  modules: ModuleSelectionRequest[];
}

export interface ModuleSelectionRequest {
  moduleKey: string;
  sourceId: string;
  amplifyModuleId: number;
  bridgeProductId?: string | undefined;
  configurationOverrides?: MigrationProperty[] | undefined;
  userMigrationEnabled?: boolean | undefined;
}

export interface RetryMigrationRequest {
  moduleKeys?: string[];
}

export interface AttachOrganizationRequest {
  organizationUuid: string;
}

export interface AttachOrganizationResult {
  migrationId: string;
  organizationUuid: string;
  ownerUuid: string | null;
}

export interface MigrationStartedResponse {
  migrationId: string;
}
